import {
  useState,
  useCallback,
  useRef,
  useEffect,
  SetStateAction,
} from 'react';
import Modal from '../../../../components/common/Modal/Modal';
import DeleteModal from '../../../../components/common/DeleteModal/DeleteModal';
import EditAudioFilenameModal from '../EditAudioFilenameModal/EditAudioFilenameModal';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import { useAppTheme } from '../../../../theme/ThemeContext';
import S from './AudioFileCardStyles';
import sendIcon from '../images/send-icon.svg';
import sendIconLight from '../images/send-icon-light.svg';

type Props = {
  id: string;
  name: string;
  durationSec?: number | null;
  sizeFormatted: string;
  audioUrl?: string | null;
  playingId?: string | null;
  existingNames?: Set<string>;
  onEdit?: (id: string, newName: string) => void;
  onDelete?: (id: string) => void;
  onSend?: (id: string) => void;
  onPlayPause?: (id: string, isPlaying: boolean) => void;
};

function PlayIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M3 2v10l8-5-8-5z" />
    </svg>
  );
}

function PauseIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <rect x="2" y="2" width="4" height="10" rx="1" />
      <rect x="8" y="2" width="4" height="10" rx="1" />
    </svg>
  );
}

export default function AudioFileCard({
  id,
  name,
  durationSec,
  sizeFormatted,
  audioUrl,
  playingId,
  existingNames,
  onEdit,
  onDelete,
  onSend,
  onPlayPause,
}: Props): JSX.Element {
  const { themeMode } = useAppTheme();
  const sendIconSrc = themeMode === 'light' ? sendIconLight : sendIcon;
  const [isPlaying, setIsPlaying] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleEnded = useCallback((): void => {
    setIsPlaying(false);
    onPlayPause?.(id, false);
  }, [id, onPlayPause]);

  useEffect(() => {
    setIsPlaying(false);
  }, [audioUrl]);

  useEffect(() => {
    if (playingId !== id && isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
    }
  }, [playingId, id, isPlaying]);

  const handlePlayPause = useCallback((): void => {
    if (audioUrl && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        onPlayPause?.(id, false);
      } else {
        audioRef.current.play().catch(() => { });
        setIsPlaying(true);
        onPlayPause?.(id, true);
      }
      return;
    }
    const next = !isPlaying;
    setIsPlaying(next);
    onPlayPause?.(id, next);
  }, [audioUrl, id, isPlaying, onPlayPause]);

  const handleEditClick = useCallback((): void => {
    setEditModalOpen(true);
  }, []);

  const handleEditClose = useCallback((): void => {
    setEditModalOpen(false);
  }, []);

  const handleUpdateFilename = useCallback((newName: string): void => {
    onEdit?.(id, newName);
    setEditModalOpen(false);
  }, [id, onEdit]);

  const handleDeleteClick = useCallback((): void => {
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback((): void => {
    onDelete?.(id);
    setDeleteModalOpen(false);
  }, [id, onDelete]);

  const handleDeleteModalClose = useCallback((): void => {
    setDeleteModalOpen(false);
  }, []);

  const handleToggleDeleteModal = useCallback((value: SetStateAction<boolean>): void => {
    setDeleteModalOpen(value);
  }, []);

  const handleSend = useCallback((): void => {
    onSend?.(id);
  }, [id, onSend]);

  const handleToggleEditModal = useCallback((value: SetStateAction<boolean>): void => {
    setEditModalOpen(value);
  }, []);

  const durationText = durationSec != null ? `${durationSec.toFixed(1)} sec` : '—';

  return (
    <>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleEnded}
          hidden
        >
          <track kind="captions" src="" label="English" />
        </audio>
      )}
      <S.Card>
        <S.Main>
          <S.TopRow>
            <S.PlayButton
              type="button"
              onClick={handlePlayPause}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </S.PlayButton>
            <S.Actions>
              <S.IconButton type="button" onClick={handleEditClick} aria-label="Edit">
                <Icon iconKey={IconKeys.EDIT} />
              </S.IconButton>
              <S.IconButton type="button" danger onClick={handleDeleteClick} aria-label="Delete">
                <Icon iconKey={IconKeys.DELETE} />
              </S.IconButton>
            </S.Actions>
          </S.TopRow>
          <S.Content>
            <S.FileName title={name}>{name}</S.FileName>
            <S.Meta>{durationText} · {sizeFormatted}</S.Meta>
          </S.Content>
        </S.Main>
        <S.SendBar>
          <S.SendButton type="button" onClick={handleSend} aria-label="Send">
            {/* <SendIcon /> */}
            <img src={sendIconSrc} alt="Send Icon" />
          </S.SendButton>
        </S.SendBar>
      </S.Card>

      {deleteModalOpen && (
        <Modal
          title="Delete audio file"
          state={deleteModalOpen}
          toggleState={handleToggleDeleteModal}
          onClose={handleDeleteModalClose}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this audio file?"
            onClose={handleDeleteModalClose}
            onDelete={handleDeleteConfirm}
          />
        </Modal>
      )}

      {editModalOpen && (
        <Modal
          title="Edit audio filename"
          state={editModalOpen}
          settings={{ minWidth: '440px' }}
          toggleState={handleToggleEditModal}
          onClose={handleEditClose}
        >
          <EditAudioFilenameModal
            initialFilename={name}
            existingNames={existingNames}
            onClose={handleEditClose}
            onUpdateFilename={handleUpdateFilename}
          />
        </Modal>
      )}
    </>
  );
}
